import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Target, BarChart3 } from "lucide-react";

const ppw = (v) => `$${Number(v || 0).toFixed(3)}/W`;

const TONES = {
  high: { c: "#EF4444", bg: "#FEF2F2", bd: "#FECACA", t: "#B91C1C", Icon: TrendingUp,
    verb: "above", sub: "Priced above your own comparable bids — expect a tougher win. Trim scope or margin if you need to be sharper." },
  low: { c: "#F59E0B", bg: "#FFFBEB", bd: "#FDE68A", t: "#B45309", Icon: TrendingDown,
    verb: "below", sub: "Aggressive versus your own comparables — competitive, but double-check you're not leaving margin on the table or underbidding scope." },
  in_line: { c: "#10B981", bg: "#ECFDF5", bd: "#A7F3D0", t: "#047857", Icon: Target,
    verb: "in line with", sub: "Right in your historical band for this size and discipline — a well-calibrated price." },
};

// Estimate benchmarking: this bid's $/W vs the estimator's own won/sent bids.
export default function BenchmarkAlertCard({ bidId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!bidId) return;
    api.get(`/bids/${bidId}/benchmark`).then((r) => setD(r.data)).catch(() => setD({ available: false }));
  }, [bidId]);
  if (!d) return null;

  if (!d.available) {
    return (
      <Card className="p-4 mb-5 border-slate-200" data-testid="benchmark-card-empty">
        <div className="flex items-center gap-2.5 text-slate-500">
          <BarChart3 className="w-4 h-4 shrink-0" />
          <div className="text-xs">{d.reason || "Not enough comparable bids to benchmark yet."}</div>
        </div>
      </Card>
    );
  }

  const tn = TONES[d.tone] || TONES.in_line;
  const vs = Math.abs(d.vs_median_pct);
  // range bar domain with a little padding
  const lo = Math.min(d.min, d.my_ppw), hi = Math.max(d.max, d.my_ppw);
  const span = hi - lo || 1;
  const pos = (v) => `${Math.max(0, Math.min(100, ((v - lo) / span) * 100))}%`;

  return (
    <Card className="p-5 mb-5" style={{ borderColor: tn.bd }} data-testid="benchmark-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: tn.bg, color: tn.c }}>
          <tn.Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-slate-800">Estimate benchmark</h3>
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-full" style={{ background: tn.bg, color: tn.t }} data-testid="benchmark-vs">
              {d.tone === "in_line" ? `±${vs}%` : `${d.vs_median_pct > 0 ? "▲" : "▼"} ${vs}%`} {tn.verb} your median
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            <span data-testid="benchmark-basis" className="font-medium text-slate-600">{d.basis}</span> · {d.sample} comparable bid{d.sample === 1 ? "" : "s"}
          </div>
          <p className="text-xs text-slate-600 mt-2 leading-relaxed">{tn.sub}</p>
        </div>
      </div>

      {/* $/W range bar: p25–p75 band, median tick, and this bid's marker */}
      <div className="mt-4 pl-1 pr-1">
        <div className="relative h-8">
          <div className="absolute top-3 left-0 right-0 h-1.5 rounded-full bg-slate-100" />
          <div className="absolute top-3 h-1.5 rounded-full bg-slate-300"
            style={{ left: pos(d.p25), width: `calc(${pos(d.p75)} - ${pos(d.p25)})` }} title="Your typical range (P25–P75)" />
          {/* median */}
          <div className="absolute -translate-x-1/2" style={{ left: pos(d.median), top: 0 }} title={`Your median ${ppw(d.median)}`}>
            <div className="w-0.5 h-8 bg-slate-500 mx-auto" />
          </div>
          {/* my marker */}
          <div className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left: pos(d.my_ppw), top: 0 }} data-testid="benchmark-marker">
            <div className="w-3.5 h-3.5 rounded-full border-2 border-white shadow" style={{ background: tn.c }} />
            <span className="text-[10px] font-mono mt-0.5 whitespace-nowrap" style={{ color: tn.t }}>{ppw(d.my_ppw)}</span>
          </div>
        </div>
        <div className="flex justify-between text-[10px] text-slate-400 font-mono mt-0.5">
          <span>{ppw(d.p25)}</span>
          <span>median {ppw(d.median)}</span>
          <span>{ppw(d.p75)}</span>
        </div>
      </div>
    </Card>
  );
}
